import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FlightPriceChange } from '@appTypes/api.types';
import { TranslatePipe } from '@ngx-translate/core';
import { FLIGHT_PRICE_CHANGED, roundCents } from '@pages/avia-page/avia-fares';
import { OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';
import { PriceFormatPipe } from '@shared/pipes/price-format.pipe';
import { AviaTraceIdComponent } from '../avia-trace-id/avia-trace-id.component';

/** What the agent decided in the dialog. */
export type AviaPriceChangedChoice = 'accept' | 'other' | 'cancel';

export interface AviaPriceChangedDialogData {
  change: FlightPriceChange;
  /** the refusal's code as the backend sent it (FLIGHT_PRICE_CHANGED) */
  code?: string | null;
  activityId?: string | null;
}

/**
 * "The price has changed" - the confirm stopped BEFORE anything was registered because the airline's price went up (flight fare
 * picker, QA doc docs features/flight-fare-picker/qa.md R-FP12 + processes/flight-booking-errors.md R-FH16 / R-FH20; plan Task 6.4).
 * Was / now / difference, the code and a copyable Trace ID; **Accept the new price** re-submits the confirm with the new total,
 * **Choose another fare** goes back to the flight's fares, **Cancel** / x stays on the checkout - nothing is booked.
 */
@Component({
  selector: 'app-avia-price-changed-dialog',
  templateUrl: './avia-price-changed-dialog.component.html',
  styleUrl: './avia-price-changed-dialog.component.scss',
  imports: [TranslatePipe, PriceFormatPipe, AviaTraceIdComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AviaPriceChangedDialogComponent implements OnDynamicModal<
  AviaPriceChangedDialogData,
  AviaPriceChangedChoice
> {
  readonly data = signal<AviaPriceChangedDialogData | null>(null);
  readonly titleId = 'avia-price-changed-title';

  readonly oldTotal = computed(() => this.data()?.change.oldTotalPrice ?? 0);
  readonly newTotal = computed(() => this.data()?.change.newTotalPrice ?? 0);
  readonly difference = computed(() => roundCents(this.newTotal() - this.oldTotal()));
  readonly currency = computed(() => this.data()?.change.priceCurrency);
  /** Tours (R-TB16): the tour's new total beside the flight part's old / new; null for a flight alone. */
  readonly packageTotal = computed(() => this.data()?.change.newPackageTotalPrice ?? null);
  readonly code = computed(() => this.data()?.code ?? FLIGHT_PRICE_CHANGED);
  /** "FZ952 / FZ989 - FLEX": what the agent is asked about. */
  readonly label = computed(() => {
    const change = this.data()?.change;
    return [change?.flightLabel, change?.fareName].filter(Boolean).join(' - ');
  });

  /**
   * An amount and its currency never part across lines (dev check 08.10.2026: "740.47 / $." wrapped at 390 px): the spaces of a formatted
   * price - thousands and before the symbol - become non-breaking inside the sentence.
   */
  together(price: string | null | undefined): string {
    return (price ?? '').replace(/ /g, ' ');
  }

  onDynamicModalClose?: (result?: AviaPriceChangedChoice) => void;

  onDynamicModal(data: AviaPriceChangedDialogData): void {
    this.data.set(data);
  }

  choose(choice: AviaPriceChangedChoice): void {
    this.onDynamicModalClose?.(choice);
  }
}
